/**
 * Topology Suggestion Engine
 *
 * Uses RBush spatial index to find the nearest classified layout entity
 * to a given map coordinate and suggest topology field values.
 */

import RBush from 'rbush';
import knn from 'rbush-knn';
import type { SiteLayout, LayoutTextEntity, TopologyFields, TopologyLevel, LayoutClassification, ClassificationPattern, ModuleSuggestion } from '../layout/types';
import { transformPoint } from '../layout/transform';
import { classifyLabel, effectiveClassification } from '../layout/classify';
import { generateModuleCellsNear } from './moduleGrid';
import type { TopologySuggestion } from '../layout/types';

// ── Spatial index item ──────────────────────────────────────

interface IndexItem {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  entity: LayoutTextEntity;
  /** The GOVERNING classification (override ?? auto), resolved once at index
   *  build — downstream reads must use this, never entity.classification. */
  cls: LayoutClassification;
  lng: number;
  lat: number;
}

// ── Search radius thresholds (meters) per level ─────────────
// Higher-level entities (stations) have larger search radius
const RADIUS_BY_LEVEL: Record<string, number> = {
  station: 200,
  inverter: 100,
  mppt: 90,
  dccb: 80,
  string: 50,
  module: 20,
  row: 60,
  zone: 150,
};
const DEFAULT_RADIUS = 60;

// ── Haversine distance (meters) ─────────────────────────────

function haversineMeters(lng1: number, lat1: number, lng2: number, lat2: number): number {
  const R = 6371000;
  const toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad;
  const dLng = (lng2 - lng1) * toRad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Point-in-polygon (ray casting) ──────────────────────────

function pointInPolygon(px: number, py: number, ring: [number, number][]): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

// ── Build spatial index ─────────────────────────────────────

export interface LayoutSpatialIndex {
  tree: RBush<IndexItem>;
  items: IndexItem[];
}

export function buildSpatialIndex(layout: SiteLayout): LayoutSpatialIndex | null {
  if (!layout.transform) return null;

  const items: IndexItem[] = [];

  for (const entity of layout.textEntities) {
    const cls = effectiveClassification(entity);
    if (!cls) continue;
    const level = cls.level;
    // Skip infrastructure, unknown, and unclassified
    if (level === 'infrastructure' || level === 'unknown') continue;

    const coords = transformPoint(layout.transform, entity.cx, entity.cy);
    if (!coords) continue;

    const [lng, lat] = coords;
    // RBush stores bounding boxes — use point (min === max)
    items.push({
      minX: lng,
      maxX: lng,
      minY: lat,
      maxY: lat,
      entity,
      cls,
      lng,
      lat,
    });
  }

  if (items.length === 0) return null;

  const tree = new RBush<IndexItem>();
  tree.load(items);

  return { tree, items };
}

// ── Suggest topology ────────────────────────────────────────

/**
 * Levels that map to TopologyFields (used for filling form fields).
 * 'row' and 'zone' are display-only, not topology form fields.
 */
export const TOPOLOGY_LEVELS: TopologyLevel[] = ['station', 'inverter', 'mppt', 'dccb', 'string', 'module'];

/**
 * Per-field prefix token alternations — the ONE source for both the
 * anchored strip below and the derive's field-value guard (composite
 * values like "2.DCCB11" are recognized by their own field's token).
 */
export const FIELD_PREFIX_TOKENS: Record<string, string> = {
  inverter: 'INV|INVERTER',
  mppt: 'MPPT',
  dccb: 'DCCB|DCB|CB|ACB',
  string: 'STR|STRING',
  module: 'MOD|MODULE',
  station: 'TS|TR|STATION',
};

/**
 * Strip known prefixes from topology values so form fields get just the identifier.
 * E.g. "INV12" → "12", "STR02" → "02", "DCCB1" → "1", "TS2" → "2"
 * Station/section values that are bare numbers pass through unchanged.
 */
const PREFIX_STRIP: Record<string, RegExp> = Object.fromEntries(
  Object.entries(FIELD_PREFIX_TOKENS).map(([k, t]) => [k, new RegExp(`^(?:${t})\\s*`, 'i')]),
);

export function stripFieldPrefix(field: string, value: string): string {
  const pattern = PREFIX_STRIP[field];
  if (!pattern) return value;
  return value.replace(pattern, '') || value;
}

/**
 * Extract hierarchy fields from a classification.
 * If the classification has no hierarchy, re-classify the value text to try to parse one.
 * Returns the fields to populate, or null if only a single-level value was found.
 *
 * `customPatterns` (the layout's classificationConfig) MUST be passed by
 * callers that have a layout in hand — omitting it silently routes the
 * site's own schema through the built-in regex cascade (T-08).
 */
export function extractHierarchyFields(
  classification: LayoutClassification,
  customPatterns?: ClassificationPattern[],
): Partial<TopologyFields> | null {
  // An EMPTY hierarchy (pattern with no capture groups, e.g. modal-created
  // custom patterns) must count as absent — otherwise the entity classifies
  // visually but contributes nothing to suggestions, and callers skip the
  // level:value fallback.
  if (classification.hierarchy && Object.keys(classification.hierarchy).length > 0) {
    return classification.hierarchy;
  }
  // Try re-classifying the value to extract hierarchy (e.g. manually added labels)
  const reclassified = classifyLabel(classification.value, customPatterns);
  if (reclassified?.hierarchy && Object.keys(reclassified.hierarchy).length > 0) {
    return reclassified.hierarchy;
  }
  return null;
}

export function suggestTopology(
  lng: number,
  lat: number,
  index: LayoutSpatialIndex,
  layout: SiteLayout,
  visibleLevels?: Set<string>,
): TopologySuggestion | null {
  // Find k nearest neighbors
  const nearest = knn(index.tree, lng, lat, 10);
  if (nearest.length === 0) return null;

  // Collect the best entity for each topology level
  const bestByLevel = new Map<TopologyLevel, { item: IndexItem; distance: number }>();

  for (const item of nearest) {
    const level = item.cls.level as TopologyLevel;

    // Skip levels that are toggled off in the layers panel
    if (visibleLevels && !visibleLevels.has(level)) continue;

    const dist = haversineMeters(lng, lat, item.lng, item.lat);
    const radius = RADIUS_BY_LEVEL[level] ?? DEFAULT_RADIUS;

    // Skip if outside search radius
    if (dist > radius) continue;

    const existing = bestByLevel.get(level);
    if (!existing || dist < existing.distance) {
      bestByLevel.set(level, { item, distance: dist });
    }
  }

  if (bestByLevel.size === 0) return null;

  // Find the absolute closest entity across all levels
  let closestEntity: IndexItem | null = null;
  let closestDistance = Infinity;
  for (const entry of bestByLevel.values()) {
    if (entry.distance < closestDistance) {
      closestDistance = entry.distance;
      closestEntity = entry.item;
    }
  }

  // "Snap" threshold: if the click is very close to a specific label (< 5m),
  // only use THAT label's hierarchy — don't mix in fields from more distant entities.
  // This prevents e.g. clicking on an inverter label from pulling in a random
  // string value from a nearby but unrelated string label.
  const SNAP_THRESHOLD = 5;
  const snapMode = closestDistance < SNAP_THRESHOLD;

  const fields: Partial<TopologyFields> = {};
  const customFields: Record<string, string> = {};
  const parts: string[] = [];

  /** Store a hierarchy entry in either standard fields or customFields */
  function storeField(key: string, value: string, overwrite = false) {
    if (TOPOLOGY_LEVELS.includes(key as TopologyLevel)) {
      if (overwrite || !fields[key as keyof TopologyFields]) {
        fields[key as keyof TopologyFields] = stripFieldPrefix(key, value);
      }
    } else if (key !== 'skip') {
      if (overwrite || !customFields[key]) {
        customFields[key] = value;
      }
    }
  }

  if (snapMode && closestEntity) {
    // Snap mode: only use the closest entity's data
    const classification = closestEntity.cls;
    const hierarchy = extractHierarchyFields(classification, layout.classificationConfig);
    if (hierarchy) {
      for (const [key, value] of Object.entries(hierarchy)) {
        if (value) storeField(key, value, true);
      }
    } else {
      const level = classification.level as keyof TopologyFields;
      if (TOPOLOGY_LEVELS.includes(classification.level as TopologyLevel)) {
        fields[level] = stripFieldPrefix(classification.level, classification.value);
      }
    }
    parts.push(`${classification.level}: ${classification.value} (${Math.round(closestDistance)}m)`);
  } else {
    // Normal mode: merge fields from best entity at each level
    for (const level of TOPOLOGY_LEVELS) {
      const entry = bestByLevel.get(level);
      if (!entry) continue;

      const classification = entry.item.cls;
      const hierarchy = extractHierarchyFields(classification, layout.classificationConfig);

      // Only set fields not already populated — closer entities (processed earlier) take priority.
      if (hierarchy) {
        for (const [key, value] of Object.entries(hierarchy)) {
          if (value) storeField(key, value);
        }
      } else {
        if (level in fields) continue;
        fields[level as keyof TopologyFields] = stripFieldPrefix(level, classification.value);
      }

      parts.push(`${level}: ${classification.value} (${Math.round(entry.distance)}m)`);
    }

    // Fill remaining fields from closest entity's hierarchy
    const closestHierarchy = closestEntity
      ? extractHierarchyFields(closestEntity.cls, layout.classificationConfig)
      : null;
    if (closestHierarchy) {
      for (const [key, value] of Object.entries(closestHierarchy)) {
        if (value) storeField(key, value);
      }
    }
  }

  if (Object.keys(fields).length === 0 && Object.keys(customFields).length === 0) return null;

  // Confidence: weighted average of (1 - distance/radius) for matched levels
  let totalWeight = 0;
  let weightedConf = 0;
  for (const level of TOPOLOGY_LEVELS) {
    const entry = bestByLevel.get(level);
    if (!entry) continue;
    const radius = RADIUS_BY_LEVEL[level] ?? DEFAULT_RADIUS;
    const conf = Math.max(0, 1 - entry.distance / radius);
    const weight = level === 'string' ? 2 : 1; // weight string matches higher
    weightedConf += conf * weight;
    totalWeight += weight;
  }
  const confidence = totalWeight > 0 ? weightedConf / totalWeight : 0;

  // ── Module number from grid ──
  // Generate the actual module grid polygons and find which module the marker
  // sits inside. This is authoritative — the polygon IS the module, so the
  // linked string entity is always correct regardless of label distance.
  let moduleSuggestion: ModuleSuggestion | undefined;
  if (layout.moduleGridConfig) {
    // Scoped: only strings whose envelope can contain this point — the
    // full-site generator measured 0.8–1.6s per call at 26k–52k polygons.
    const gridGeoJSON = generateModuleCellsNear(layout, lng, lat);
    const entityById = new Map(layout.textEntities.map(e => [e.id, e]));

    for (const feature of gridGeoJSON.features) {
      if (feature.geometry.type !== 'Polygon') continue;
      const ring = (feature.geometry as GeoJSON.Polygon).coordinates[0] as [number, number][];
      if (!pointInPolygon(lng, lat, ring)) continue;

      // Marker is inside this module polygon
      const p = feature.properties!;
      const entityId: string = p.entityId;
      const moduleNumber: number = p.moduleNumber;
      const totalModules: number = p.totalModules;
      const row: number = p.row;

      moduleSuggestion = {
        moduleNumber,
        row,
        column: moduleNumber, // approximate — actual column handled by grid
        totalModules,
        stringEntityId: entityId,
      };
      fields.module = String(moduleNumber);

      // Overwrite string (and higher-level) fields from the matched string's
      // hierarchy — read through effectiveClassification, matching how the
      // generator selected the string (membership and fields from ONE source).
      const stringEntity = entityById.get(entityId);
      const stringCls = stringEntity ? effectiveClassification(stringEntity) : undefined;
      if (stringCls) {
        const gridHierarchy = extractHierarchyFields(stringCls, layout.classificationConfig);
        if (gridHierarchy) {
          for (const [key, value] of Object.entries(gridHierarchy)) {
            if (value) storeField(key, value, true);
          }
        } else {
          fields.string = stripFieldPrefix('string', stringCls.value);
        }
      }
      break; // found the module — done
    }
  }

  return {
    fields,
    confidence,
    nearestEntity: closestEntity
      ? {
          id: closestEntity.entity.id,
          text: closestEntity.entity.text,
          distanceMeters: closestDistance,
        }
      : undefined,
    explanation: parts.join(', '),
    moduleSuggestion,
    customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
  };
}
